/**
 * Archive vacature — client ajax for search, tabs, drawer and pagination.
 *
 * Intercepts the user actions on the /vacatures/ archive and swaps the
 * results fragment via REST without a full page reload. Falls back to a
 * normal form submit when JS is unavailable (form/anchor markup is intact).
 *
 * Drawer-state is instant-apply: filter-drawer.js dispatches
 * `filterdrawer:change` per input-change en `filterdrawer:reset` op de
 * "Toon alles" knop — geen submit-knop nodig.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin( ScrollTrigger );

const REST_BASE = ( window.HellemaArchive && window.HellemaArchive.restUrl )
	? window.HellemaArchive.restUrl
	: '/wp-json/hellema/v1/vacatures';
const DEBOUNCE_MS = 350;

const root = document.querySelector( '[data-archive-vacature]' );

if ( root ) {
	initArchive( root );
}

function initArchive( rootEl ) {
	const resultsEl   = rootEl.querySelector( '[data-vacature-results]' );
	const searchForm  = rootEl.querySelector( '[data-archive-form="search"]' );
	const searchInput = searchForm && searchForm.querySelector( 'input[name="q"]' );
	const tabsEl      = rootEl.querySelector( '[data-archive-tabs]' );
	const moreFilters = rootEl.querySelector( '[data-archive-side-filters]' );
	const topReset    = rootEl.querySelector( '[data-archive-reset]' );
	const drawerEl    = document.querySelector( '[data-filter-drawer]' );
	const drawerForm  = drawerEl && drawerEl.querySelector( '[data-filter-drawer-form]' );

	if ( ! resultsEl ) {
		return;
	}

	const state = readStateFromUrl();
	let activeFetch = null;

	function readStateFromUrl() {
		const params = new URLSearchParams( window.location.search );
		return {
			q: params.get( 'q' ) || '',
			cat: params.get( 'cat' ) || '',
			opleiding: params.getAll( 'opleiding[]' ),
			ervaring: params.getAll( 'ervaring[]' ),
			uren: parseInt( params.get( 'uren' ) || '0', 10 ) || 0,
			paged: parseInt( params.get( 'paged' ) || '1', 10 ) || 1,
		};
	}

	function stateToParams( s ) {
		const params = new URLSearchParams();
		if ( s.q ) params.set( 'q', s.q );
		if ( s.cat ) params.set( 'cat', s.cat );
		s.opleiding.forEach( ( v ) => params.append( 'opleiding[]', v ) );
		s.ervaring.forEach( ( v ) => params.append( 'ervaring[]', v ) );
		if ( s.uren ) params.set( 'uren', String( s.uren ) );
		if ( s.paged > 1 ) params.set( 'paged', String( s.paged ) );
		return params;
	}

	function hasAnyFilter( s ) {
		return !! ( s.q || s.cat || s.opleiding.length || s.ervaring.length || s.uren );
	}
	function hasSideFilters( s ) {
		return !! ( s.opleiding.length || s.ervaring.length || s.uren );
	}

	function syncUrl() {
		const params = stateToParams( state );
		const qs = params.toString();
		const newUrl = window.location.pathname + ( qs ? '?' + qs : '' );
		window.history.replaceState( state, '', newUrl );
	}

	function syncTabsActive() {
		if ( ! tabsEl ) return;
		tabsEl.querySelectorAll( '[data-archive-tab]' ).forEach( ( tab ) => {
			const isActive = tab.dataset.cat === state.cat;
			tab.classList.toggle( 'is-active', isActive );
			if ( isActive ) {
				tab.setAttribute( 'aria-current', 'page' );
			} else {
				tab.removeAttribute( 'aria-current' );
			}
		} );
	}

	function syncMoreFiltersActive() {
		if ( moreFilters ) {
			moreFilters.classList.toggle( 'is-active', hasSideFilters( state ) );
		}
	}

	function syncTopReset() {
		if ( ! topReset ) return;
		const visible = hasAnyFilter( state );
		topReset.hidden = ! visible;
		topReset.classList.toggle( 'is-visible', visible );
	}

	function syncDrawerInputs() {
		if ( ! drawerForm ) return;
		drawerForm.querySelectorAll( 'input[name="opleiding[]"]' ).forEach( ( el ) => {
			el.checked = state.opleiding.includes( el.value );
		} );
		drawerForm.querySelectorAll( 'input[name="ervaring[]"]' ).forEach( ( el ) => {
			el.checked = state.ervaring.includes( el.value );
		} );
		const range = drawerForm.querySelector( '[data-filter-drawer-range]' );
		if ( range ) range.value = String( state.uren || 0 );
		const rangeOut = drawerForm.querySelector( '[data-filter-drawer-range-output]' );
		if ( rangeOut ) rangeOut.textContent = `${ state.uren || 0 } uur`;
	}

	function syncAllUi() {
		syncUrl();
		syncTabsActive();
		syncMoreFiltersActive();
		syncTopReset();
	}

	async function fetchAndSwap( opts = {} ) {
		const { scrollToGrid = false } = opts;

		if ( activeFetch ) {
			activeFetch.abort();
		}

		const controller = new AbortController();
		activeFetch = controller;

		resultsEl.setAttribute( 'aria-busy', 'true' );
		resultsEl.classList.add( 'is-loading' );

		const params = stateToParams( state );
		const url = REST_BASE + ( params.toString() ? '?' + params.toString() : '' );

		try {
			const response = await fetch( url, {
				signal: controller.signal,
				headers: { 'Accept': 'application/json' },
				credentials: 'same-origin',
			} );

			if ( ! response.ok ) {
				throw new Error( `Request failed: ${ response.status }` );
			}

			const data = await response.json();
			resultsEl.innerHTML = data.html || '';
			syncAllUi();
			bindPagination();

			// Grid-hoogte verandert na de swap — ScrollTrigger-posities voor
			// h2-reveals onder de listing (USP/Collega's)
			// raken anders mis-aligned. Wachten op layout-stable via
			// requestAnimationFrame voor de refresh.
			requestAnimationFrame( () => ScrollTrigger.refresh() );

			if ( scrollToGrid ) {
				const top = resultsEl.getBoundingClientRect().top + window.scrollY - 120;
				window.scrollTo( { top, behavior: 'smooth' } );
			}
		} catch ( err ) {
			if ( err.name !== 'AbortError' ) {
				console.error( '[archive-vacature] fetch failed', err );
			}
		} finally {
			if ( activeFetch === controller ) {
				activeFetch = null;
				resultsEl.setAttribute( 'aria-busy', 'false' );
				resultsEl.classList.remove( 'is-loading' );
			}
		}
	}

	// --- Search input (debounced + submit) -------------------------------
	if ( searchInput ) {
		let debounceTimer = null;
		searchInput.addEventListener( 'input', () => {
			window.clearTimeout( debounceTimer );
			debounceTimer = window.setTimeout( () => {
				state.q = searchInput.value.trim();
				state.paged = 1;
				fetchAndSwap();
			}, DEBOUNCE_MS );
		} );
	}
	if ( searchForm ) {
		searchForm.addEventListener( 'submit', ( e ) => {
			e.preventDefault();
			if ( searchInput ) state.q = searchInput.value.trim();
			state.paged = 1;
			fetchAndSwap();
		} );
	}

	// --- Category tabs ---------------------------------------------------
	if ( tabsEl ) {
		tabsEl.addEventListener( 'click', ( e ) => {
			const tab = e.target.closest( '[data-archive-tab]' );
			if ( ! tab ) return;
			e.preventDefault();
			const newCat = tab.dataset.cat || '';
			state.cat = ( state.cat === newCat ) ? '' : newCat; // toggle off when re-clicking active
			state.paged = 1;
			fetchAndSwap( { scrollToGrid: true } );
		} );
	}

	// --- Filter drawer (instant apply via custom events) -----------------
	if ( drawerForm ) {
		drawerForm.addEventListener( 'filterdrawer:change', () => {
			const fd = new FormData( drawerForm );
			state.opleiding = fd.getAll( 'opleiding[]' ).map( String );
			state.ervaring = fd.getAll( 'ervaring[]' ).map( String );
			state.uren = parseInt( fd.get( 'uren' ) || '0', 10 ) || 0;
			state.paged = 1;
			fetchAndSwap();
		} );

		// Drawer "Toon alles" — alleen side-filters; top reset clear ook q/cat.
		drawerForm.addEventListener( 'filterdrawer:reset', () => {
			state.opleiding = [];
			state.ervaring = [];
			state.uren = 0;
			state.paged = 1;
			fetchAndSwap();
		} );

		// No-JS fallback: submit blokkeren als JS toch loadt.
		drawerForm.addEventListener( 'submit', ( e ) => {
			e.preventDefault();
			drawerForm.dispatchEvent( new CustomEvent( 'filterdrawer:change', { bubbles: true } ) );
		} );
	}

	// --- Top "Toon alles" — clear álles ----------------------------------
	if ( topReset ) {
		topReset.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			state.q = '';
			state.cat = '';
			state.opleiding = [];
			state.ervaring = [];
			state.uren = 0;
			state.paged = 1;
			if ( searchInput ) searchInput.value = '';
			syncDrawerInputs();
			fetchAndSwap( { scrollToGrid: true } );
		} );
	}

	// --- Pagination ------------------------------------------------------
	function bindPagination() {
		const pagEl = resultsEl.querySelector( '[data-archive-pagination]' );
		if ( ! pagEl ) return;
		pagEl.addEventListener( 'click', ( e ) => {
			const link = e.target.closest( 'a.page-numbers' );
			if ( ! link ) return;
			e.preventDefault();
			const url = new URL( link.href );
			const newPaged = parseInt( url.searchParams.get( 'paged' ) || '1', 10 ) || 1;
			state.paged = newPaged;
			fetchAndSwap( { scrollToGrid: true } );
		} );
	}
	bindPagination();

	// Initial UI sync (server-rendered state matches URL, but make sure
	// classes are in sync after JS init).
	syncAllUi();
}
